import { createFeatureSelector, createSelector } from '@ngrx/store';
import { UserRole } from '../../models/access';
import {
  selectAllGuardianIds,
  selectLoggedInGuardian,
  selectLoggedInGuardianId,
  selectSelectedGuardian,
  selectSelectedGuardianUserType,
} from '../guardian/guardian.selectors';
import { AccessState } from './access.reducer';

export const selectAccessState = createFeatureSelector<AccessState>('access');

export const selectUserAccessContext = createSelector(selectAccessState, (state) => state.userAccessContext);

export const selectCurrentUserRole = createSelector(selectAccessState, (state) => state.highestUserRole);

export const selectAccessLoading = createSelector(selectAccessState, (state) => state.loading);

export const selectAccessError = createSelector(selectAccessState, (state) => state.error);

export const selectCurrentContext = createSelector(selectAccessState, (state) => state.currentContext);

export const selectCurrentComponentName = createSelector(selectCurrentContext, (context) => context?.componentName);

export const selectCurrentStudentId = createSelector(selectCurrentContext, (context) => context?.studentId);

export const selectCurrentGuardianId = createSelector(selectCurrentContext, (context) => context?.guardianId);

export const selectStudentIds = createSelector(selectUserAccessContext, (contexts) => {
  if (!contexts) return [];
  return contexts.flatMap((context) => context.studentIds);
});

export const selectGuardianIds = createSelector(selectAllGuardianIds, (guardianIds) => guardianIds);

export const selectHasAccessData = createSelector(
  selectAccessState,
  (state) => !!state.userAccessContext && state.userAccessContext.length > 0,
);

export const selectUserAccessByCompany = (companyCode: string) =>
  createSelector(
    selectUserAccessContext,
    (contexts) => contexts?.find((context) => context.companyCode === companyCode) || null,
  );

// Helper function to resolve context - use provided params or fall back to current context
const resolveContext = (state: AccessState, componentName?: string, studentId?: string, guardianId?: string) => {
  return {
    componentName: componentName || state.currentContext?.componentName,
    studentId: studentId || state.currentContext?.studentId,
    guardianId: guardianId || state.currentContext?.guardianId,
  };
};

// Helper: find the highest role for a guardian by matching their account links to access contexts
const resolveGuardianRole = (state: AccessState, guardianId: string): UserRole | null => {
  const links = state.guardianAccountLinks.filter((link) => link.guardianId === guardianId);
  if (!links.length || !state.userAccessContext) return null;

  let highestRole: UserRole | null = null;
  for (const link of links) {
    const context = state.userAccessContext.find(
      (ctx) => ctx.accountCode === link.accountCode && ctx.companyCode === link.companyCode,
    );
    if (context) {
      if (context.userRole === 'Admin') return 'Admin';
      highestRole = context.userRole;
    }
  }
  return highestRole;
};

// Helper selector to determine the effective user role for a given context
export const selectEffectiveUserRole = (studentId?: string, guardianId?: string) =>
  createSelector(selectAccessState, (state) => {
    const resolvedContext = resolveContext(state, undefined, studentId, guardianId);

    if (!state.userAccessContext) return state.highestUserRole;

    // If studentId is provided, find the context that contains this student
    if (resolvedContext.studentId) {
      const context = state.userAccessContext.find((ctx) => ctx.studentIds.includes(resolvedContext.studentId!));
      if (context) return context.userRole;
    }

    // If guardianId is provided, resolve role via guardianAccountLinks
    if (resolvedContext.guardianId) {
      const role = resolveGuardianRole(state, resolvedContext.guardianId);
      if (role) return role;
    }

    // Fall back to highest user role
    return state.highestUserRole;
  });

// Main selector for checking permissions with optional context (falls back to current context)
export const selectCanAccessComponent = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(
    selectAccessState,
    selectLoggedInGuardianId,
    selectAllGuardianIds,
    selectEffectiveUserRole(studentId, guardianId),
    (state, loggedInGuardianId, guardianIds, effectiveRole) => {
      const resolvedContext = resolveContext(state, componentName, studentId, guardianId);

      // First check if we have any access data
      if (!state.userAccessContext || !state.componentAccessContext) {
        const reason = 'No access data available';
        console.warn(`Access denied - ${reason}`, {
          componentName: resolvedContext.componentName,
          studentId: resolvedContext.studentId,
          guardianId: resolvedContext.guardianId,
          effectiveRole,
          hasUserAccessContext: !!state.userAccessContext,
          hasComponentAccessContext: !!state.componentAccessContext,
        });
        return {
          canAccess: false,
          reason,
        };
      }

      if (!resolvedContext.componentName) {
        const reason = 'No component name specified and no current context set';
        return {
          canAccess: false,
          reason,
        };
      }

      // Own profile: grant access to all pages except AccessFlowPage
      if (resolvedContext.guardianId && loggedInGuardianId === resolvedContext.guardianId) {
        if (resolvedContext.componentName === 'AccessFlowPage') {
          return {
            canAccess: false,
            reason: 'Cannot access own AccessFlowPage',
          };
        }

        const ownComponentAccess = state.componentAccessContext.find(
          (access) => access.componentName === resolvedContext.componentName && access.userRole === effectiveRole,
        );

        return {
          canAccess: true,
          permissions: ownComponentAccess?.permissions,
          effectiveRole,
        };
      }

      // Find component permissions for the effective role
      const componentAccess = state.componentAccessContext.find(
        (access) => access.componentName === resolvedContext.componentName && access.userRole === effectiveRole,
      );

      if (!componentAccess) {
        const reason = `No permissions found for component: ${resolvedContext.componentName} with role: ${effectiveRole}`;
        console.warn(`Access denied - ${reason}`, {
          componentName: resolvedContext.componentName,
          effectiveRole,
          availableComponents: state.componentAccessContext.map((c) => ({
            componentName: c.componentName,
            userRole: c.userRole,
            permissions: c.permissions,
          })),
        });
        return {
          canAccess: false,
          reason,
        };
      }

      // If studentId is provided, check if user has access to that student
      if (resolvedContext.studentId) {
        const hasStudentAccess = state.userAccessContext.some((context) =>
          context.studentIds.includes(resolvedContext.studentId!),
        );

        if (!hasStudentAccess) {
          const reason = `No access to student: ${resolvedContext.studentId}`;
          console.warn(`Access denied - ${reason}`, {
            componentName: resolvedContext.componentName,
            studentId: resolvedContext.studentId,
            effectiveRole,
            componentPermissions: componentAccess.permissions,
            availableStudentIds: state.userAccessContext.flatMap((ctx) => ctx.studentIds),
          });
          return {
            canAccess: false,
            reason,
          };
        }
      }

      // If guardianId is provided, check if user has access to that guardian
      if (resolvedContext.guardianId) {
        const hasGuardianAccess = guardianIds.includes(resolvedContext.guardianId);

        if (!hasGuardianAccess) {
          const reason = `No access to guardian: ${resolvedContext.guardianId}`;
          console.warn(`Access denied - ${reason}`, {
            componentName: resolvedContext.componentName,
            guardianId: resolvedContext.guardianId,
            effectiveRole,
            componentPermissions: componentAccess.permissions,
            availableGuardianIds: guardianIds,
          });
          return {
            canAccess: false,
            reason,
          };
        }
      }

      // Return the component permissions
      return {
        canAccess: componentAccess.permissions.canView,
        permissions: componentAccess.permissions,
        effectiveRole: effectiveRole,
      };
    },
  );

// Unified permission selectors that work with optional context
export const selectCanViewComponent = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(
    selectCanAccessComponent(componentName, studentId, guardianId),
    (accessResult) => accessResult.canAccess,
  );

export const selectCanEditComponent = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(selectCanAccessComponent(componentName, studentId, guardianId), (accessResult) =>
    'permissions' in accessResult ? accessResult.permissions?.canEdit || false : false,
  );

export const selectCanCreateComponent = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(selectCanAccessComponent(componentName, studentId, guardianId), (accessResult) =>
    'permissions' in accessResult ? accessResult.permissions?.canCreate || false : false,
  );

export const selectCanDeleteComponent = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(selectCanAccessComponent(componentName, studentId, guardianId), (accessResult) =>
    'permissions' in accessResult ? accessResult.permissions?.canDelete || false : false,
  );

export const selectCanExecuteComponent = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(selectCanAccessComponent(componentName, studentId, guardianId), (accessResult) =>
    'permissions' in accessResult ? accessResult.permissions?.canExecute || false : false,
  );

export const selectPermissions = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(selectCanAccessComponent(componentName, studentId, guardianId), (accessResult) =>
    'permissions' in accessResult
      ? accessResult.permissions
      : {
          canView: false,
          canEdit: false,
          canCreate: false,
          canDelete: false,
          canExecute: false,
        },
  );

export const selectEffectiveRole = (componentName?: string, studentId?: string, guardianId?: string) =>
  createSelector(selectCanAccessComponent(componentName, studentId, guardianId), (accessResult) =>
    'effectiveRole' in accessResult ? accessResult.effectiveRole : null,
  );

// Default instances for common usage (these are properly memoized)
export const selectCanViewCurrentComponent = selectCanViewComponent();
export const selectCanEditCurrentComponent = selectCanEditComponent();
export const selectCanCreateCurrentComponent = selectCanCreateComponent();
export const selectCanDeleteCurrentComponent = selectCanDeleteComponent();
export const selectCanExecuteCurrentComponent = selectCanExecuteComponent();
export const selectCurrentPermissions = selectPermissions();
export const selectCurrentEffectiveRole = selectEffectiveRole();

// Helper selector to check if user has any access to a specific student
export const selectHasStudentAccess = (studentId: string) =>
  createSelector(selectUserAccessContext, (contexts) => {
    if (!contexts) return false;
    return contexts.some((context) => context.studentIds.includes(studentId));
  });

// Helper selector to check if user has any access to a specific guardian
export const selectHasGuardianAccess = (guardianId: string) =>
  createSelector(selectAllGuardianIds, (allGuardianIds) => {
    return allGuardianIds.includes(guardianId);
  });

// Get student IDs from access state (since we only store IDs, not full objects)
export const selectStudentIdsFromAccess = createSelector(selectUserAccessContext, (contexts) => {
  if (!contexts) return [];
  // Extract unique student IDs from all access contexts
  const allStudentIds = contexts.flatMap((context) => context.studentIds || []);
  // Remove duplicates
  return [...new Set(allStudentIds)];
});

export const selectStudentIdsWithAccessByType = (userType: 'Admin' | 'Co-Parent') =>
  createSelector(selectStudentIdsFromAccess, selectLoggedInGuardian, (studentIds, loggedInGuardian) => {
    if (!loggedInGuardian?.learnerAccess) return [];

    const accessStudentIds = loggedInGuardian.learnerAccess
      .filter((access) => access.userType === userType)
      .map((access) => access.learnerId);

    return studentIds.filter((id) => accessStudentIds.includes(id));
  });

// Whether the logged-in user is the selected guardian and not an Admin (co-parent scenario)
export const selectIsLoggedInCoParent = createSelector(
  selectLoggedInGuardian,
  selectSelectedGuardian,
  selectSelectedGuardianUserType,
  (currentUser, selectedGuardian, guardianUserType) => {
    if (currentUser && selectedGuardian && currentUser.id === selectedGuardian.id && guardianUserType !== 'Admin') {
      return true;
    }

    return false;
  },
);

export const selectAllStudentIdsSelectedGuardianHasAccessTo = createSelector(
  selectAccessState,
  selectSelectedGuardian,
  (state, selectedGuardian) => {
    if (!state.userAccessContext || !selectedGuardian?.id) return [];

    // Find accounts this guardian belongs to via guardianAccountLinks
    const guardianLinks = state.guardianAccountLinks.filter((link) => link.guardianId === selectedGuardian.id);

    // Find all student IDs from contexts matching the guardian's accounts
    const studentIds = state.userAccessContext
      .filter((ctx) =>
        guardianLinks.some((link) => link.accountCode === ctx.accountCode && link.companyCode === ctx.companyCode),
      )
      .flatMap((ctx) => ctx.studentIds);

    // Return unique student IDs
    return Array.from(new Set(studentIds));
  },
);

export const selectSelectedAccountCodes = createSelector(
  selectAccessState,
  (state: AccessState) => state.selectedAccountCodes,
);

export const selectViewableGuardianIds = (componentName: string) =>
  createSelector(
    selectAccessState,
    selectLoggedInGuardianId,
    selectAllGuardianIds,
    (state, loggedInGuardianId, allGuardianIds) => {
      if (!state.userAccessContext || !state.componentAccessContext) return new Set<string>();

      const viewableIds = new Set<string>();

      // For each access context, check if the role grants canView for the component.
      // If it does, only add guardians linked to that specific account.
      for (const context of state.userAccessContext) {
        const componentAccess = state.componentAccessContext.find(
          (ca) => ca.componentName === componentName && ca.userRole === context.userRole,
        );

        if (componentAccess?.permissions.canView) {
          const linkedGuardianIds = state.guardianAccountLinks
            .filter((link) => link.accountCode === context.accountCode && link.companyCode === context.companyCode)
            .map((link) => link.guardianId)
            .filter((id) => allGuardianIds.includes(id));

          linkedGuardianIds.forEach((id) => viewableIds.add(id));
        }
      }

      // Always include the logged-in user's own ID
      if (loggedInGuardianId) viewableIds.add(loggedInGuardianId);

      return viewableIds;
    },
  );

export const selectExecutableGuardianIds = (componentName: string) =>
  createSelector(selectAccessState, selectAllGuardianIds, (state, allGuardianIds) => {
    if (!state.userAccessContext || !state.componentAccessContext) return new Set<string>();

    const executableIds = new Set<string>();

    // For each access context, check if the role grants canExecute for the component.
    // If it does, only add guardians linked to that specific account.
    for (const context of state.userAccessContext) {
      const componentAccess = state.componentAccessContext.find(
        (ca) => ca.componentName === componentName && ca.userRole === context.userRole,
      );

      if (componentAccess?.permissions.canExecute) {
        const linkedGuardianIds = state.guardianAccountLinks
          .filter((link) => link.accountCode === context.accountCode && link.companyCode === context.companyCode)
          .map((link) => link.guardianId)
          .filter((id) => allGuardianIds.includes(id));

        linkedGuardianIds.forEach((id) => executableIds.add(id));
      }
    }

    return executableIds;
  });

export const selectAccessAlertAdmin = createSelector(selectAccessState, (state) => state.accessAlertAdmin);
